import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { ModalBase } from "@/components/common/ModalBase/ModalBase"
import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  getDataRoomDownloadUrl,
  getDataRoomFileContent,
} from "@/services/api/dataRoomService"
import type { DataRoomFile } from "@/types/dataRoom"
import { IconDownload, IconX } from "@tabler/icons-react"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { previewKind } from "./previewKind"
import { SpreadsheetPreview } from "./SpreadsheetPreview"

// The backend's presigned download URL is valid for 5 minutes; the preview
// stays comfortably under that and refreshes while it remains open.
const PREVIEW_URL_STALE_TIME_MS = 4 * 60 * 1000
const PREVIEW_URL_GC_TIME_MS = 5 * 60 * 1000
const PREVIEW_URL_REFRESH_INTERVAL_MS = 4 * 60 * 1000

// Raw file bytes (spreadsheet/image) don't expire like a presigned URL — a
// generous stale time just avoids refetching the same bytes on every reopen
// within the window.
const PREVIEW_CONTENT_STALE_TIME_MS = 5 * 60 * 1000

interface ImagePreviewProps {
  data: ArrayBuffer
  mimetype: string
  fileName: string
}

const ImagePreview: React.FC<ImagePreviewProps> = ({
  data,
  mimetype,
  fileName,
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  useEffect(() => {
    const blob = new Blob([data], {
      type: mimetype || "application/octet-stream",
    })
    const url = URL.createObjectURL(blob)
    setObjectUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [data, mimetype])

  if (!objectUrl) return null

  return (
    <div className="flex flex-1 items-center justify-center overflow-auto">
      <img
        src={objectUrl}
        alt={fileName}
        className="max-h-[80vh] max-w-full object-contain"
      />
    </div>
  )
}

interface PreviewErrorFallbackProps {
  onDownload: () => void
}

const PreviewErrorFallback: React.FC<PreviewErrorFallbackProps> = ({
  onDownload,
}) => {
  const { translate } = useTranslation()
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3">
      <Typography variant="textS" className="text-grey5">
        {translate("DATA_ROOM/PREVIEW_ERROR")}
      </Typography>
      <Button variant="outline" onClick={onDownload}>
        {translate("DATA_ROOM/DOWNLOAD_TOOLTIP")}
      </Button>
    </div>
  )
}

export interface DataRoomPreviewModalProps {
  file: DataRoomFile
  onClose: () => void
  onDownload: (file: DataRoomFile) => void
}

// Mounted only while a preview is open, so reopening the same (or a
// different) file is a fresh mount and always refetches. The content query's
// `queryFn` forwards react-query's own AbortSignal into the request, so
// unmounting (or the query being superseded by a refetch) actually aborts
// the in-flight request instead of merely discarding its eventual result —
// a preview closed before its data arrives never renders stale bytes for a
// file that's no longer open, and doesn't keep downloading it either.
export const DataRoomPreviewModal: React.FC<DataRoomPreviewModalProps> = ({
  file,
  onClose,
  onDownload,
}) => {
  const { translate } = useTranslation()
  const kind = previewKind(file.fileName, file.mimetype)

  const {
    data: previewUrl,
    isFetching: isUrlFetching,
    isError: isUrlError,
  } = useQuery({
    queryKey: ["dataRoomDownloadUrl", file.id],
    queryFn: () => getDataRoomDownloadUrl(file.id),
    staleTime: PREVIEW_URL_STALE_TIME_MS,
    gcTime: PREVIEW_URL_GC_TIME_MS,
    refetchOnMount: "always",
    refetchInterval: PREVIEW_URL_REFRESH_INTERVAL_MS,
    enabled: kind === "pdf",
  })

  // Gate only the first load of this mount: a background refresh keeps the
  // still-valid URL on screen until the new one lands.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  useEffect(() => {
    if (!isUrlFetching && previewUrl) setHasLoadedOnce(true)
  }, [isUrlFetching, previewUrl])

  const {
    data: fileContent,
    isPending: isContentPending,
    isError: isContentError,
  } = useQuery({
    queryKey: ["dataRoomFileContent", file.id],
    queryFn: ({ signal }) => getDataRoomFileContent(file.id, signal),
    staleTime: PREVIEW_CONTENT_STALE_TIME_MS,
    enabled: kind === "sheet" || kind === "image",
  })

  const handleDownload = () => onDownload(file)

  // Escape closes wherever focus sits, including when it never left the
  // triggering row (nothing inside the modal would receive the key then).
  useEffect(() => {
    const handleKeyDown = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        keyboardEvent.preventDefault()
        onClose()
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  // Same header as the knowledge base preview: file name left, actions and
  // the close control on the right, so the close button sits where users
  // already expect it in every document preview.
  return (
    <ModalBase showSidebarLeft>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={file.fileName}
        className="flex h-full flex-col"
      >
        <div className="mb-4 flex items-center gap-2 border-b border-grey4 py-2">
          <Typography
            variant="textS"
            className="min-w-0 flex-1 truncate px-4 text-grey5"
          >
            {file.fileName}
          </Typography>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-grey5 hover:text-white"
              data-testid="data-room-preview-download"
              onClick={handleDownload}
            >
              <IconDownload size={16} />
              {translate("DATA_ROOM/DOWNLOAD_TOOLTIP")}
            </Button>
            <Button
              variant="ghost"
              onClick={onClose}
              data-testid="data-room-preview-close"
              aria-label={translate("CLOSE")}
            >
              <IconX />
            </Button>
          </div>
        </div>

        {kind === "pdf" &&
          (isUrlError ? (
            <PreviewErrorFallback onDownload={handleDownload} />
          ) : !hasLoadedOnce || !previewUrl ? (
            <div className="flex flex-1 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <iframe
              src={previewUrl}
              title={file.fileName}
              className="h-full w-full flex-1 rounded"
              data-testid="data-room-preview-iframe"
            >
              {translate("PDF_VIEW_NOT_SUPPORTED")}
            </iframe>
          ))}

        {(kind === "sheet" || kind === "image") &&
          (isContentPending ? (
            <div className="flex flex-1 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : isContentError || !fileContent ? (
            <PreviewErrorFallback onDownload={handleDownload} />
          ) : kind === "sheet" ? (
            <SpreadsheetPreview data={fileContent} />
          ) : (
            <ImagePreview
              data={fileContent}
              mimetype={file.mimetype}
              fileName={file.fileName}
            />
          ))}

        {kind === "none" && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3">
            <Typography variant="textS" className="text-grey5">
              {translate("DATA_ROOM/PREVIEW_UNAVAILABLE")}
            </Typography>
            <Button variant="outline" onClick={handleDownload}>
              {translate("DATA_ROOM/DOWNLOAD_TOOLTIP")}
            </Button>
          </div>
        )}
      </div>
    </ModalBase>
  )
}
